import type { N8nWorkflow } from './types';

export type WorkflowFormat = 'n8n' | 'ewa' | 'unknown';

/**
 * Detect the format of a pasted workflow JSON
 */
export function detectWorkflowFormat(json: unknown): WorkflowFormat {
  if (!json || typeof json !== 'object') {
    return 'unknown';
  }

  const obj = json as Record<string, unknown>;

  // n8n indicators
  const hasConnections = typeof obj.connections === 'object' && obj.connections !== null;
  const hasN8nNodes = Array.isArray(obj.nodes) && obj.nodes.some((n: unknown) => {
    const node = n as Record<string, unknown>;
    return typeof node.typeVersion === 'number' ||
           (typeof node.type === 'string' && (
             node.type.startsWith('n8n-nodes-') ||
             node.type.startsWith('@n8n/')
           ));
  });
  const hasMeta = typeof obj.meta === 'object' && obj.meta !== null;

  if (hasConnections && (hasN8nNodes || hasMeta)) {
    return 'n8n';
  }

  // EWA indicators
  const hasEdges = Array.isArray(obj.edges);
  const hasEwaNodes = Array.isArray(obj.nodes) && obj.nodes.some((n: unknown) => {
    const node = n as Record<string, unknown>;
    return typeof node.data === 'object' && node.data !== null;
  });

  if (hasEdges && hasEwaNodes) {
    return 'ewa';
  }

  return 'unknown';
}

/**
 * Validate n8n workflow structure
 */
export function isValidN8nWorkflow(json: unknown): json is N8nWorkflow {
  if (!json || typeof json !== 'object') return false;

  const obj = json as Record<string, unknown>;

  // Must have nodes array
  if (!Array.isArray(obj.nodes)) return false;

  // Must have connections object
  if (typeof obj.connections !== 'object' || obj.connections === null) return false;

  // Validate nodes have required fields
  return obj.nodes.every((node: unknown) => {
    if (!node || typeof node !== 'object') return false;
    const n = node as Record<string, unknown>;
    return (
      typeof n.id === 'string' &&
      typeof n.name === 'string' &&
      typeof n.type === 'string' &&
      Array.isArray(n.position) &&
      n.position.length === 2
    );
  });
}

/**
 * Try to parse JSON from a string
 */
export function tryParseJson(text: string): { success: true; data: unknown } | { success: false; error: string } {
  try {
    const data = JSON.parse(text);
    return { success: true, data };
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Invalid JSON' };
  }
}
